import React, { useState, useEffect } from "react";
import "./RequestGatepass.css";
import { Button, ImageWithFallback } from "../../components/Utilities";
import { apiService } from "../../../services/apiService";
import { AddDriver } from "./AddDriver";
import { useNavigate } from "react-router-dom";
import rc_yes from "../../assets/rc-yes.svg";
import rc_no from "../../assets/rc-no.svg";
import pro from "../../assets/person.svg";
import loc from "../../assets/locCard.svg";
import { Loading } from "../../components/Loading";
import { buildCleanMapIframe, getMapUrl } from "../../MyCars/Inspection";

const DriverDropdown = ({
  drivers,
  selectedDriver,
  onSelectDriver,
  onAddDriver,
}) => {
  const handleEvaluatorChange = (e) => {
    const value = e.target.value;
    if (value === "add-driver") {
      onAddDriver();
    } else {
      const selected = drivers.find((driver) => driver.id === value) || null;
      onSelectDriver(selected);
    }
  };

  return (
    <select
      value={selectedDriver?.id || ""}
      onChange={handleEvaluatorChange}
      className="evaluator-select"
    >
      <option value="">Choose Driver</option>
      {drivers.map((driver) => (
        <option key={driver.id} value={driver.id}>
          {driver.name}
        </option>
      ))}
      <option value="add-driver">Add a Driver</option>
    </select>
  );
};

export const RequestDeliveryGatepassModal = ({
  isOpen,
  onClose,
  cars,
  onRequestGatepass,
  yardGroups,
  clickedCarId, // NEW: ID of the car that opened the modal
}) => {
  const [profilePicture, setProfilePicture] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedCarIds, setSelectedCarIds] = useState([]);
  const [isAddDriverModalOpen, setIsAddDriverModalOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driverLoading, setDriverLoading] = useState(true);
  const [driverError, setDriverError] = useState(null);
  const [gatepassLoading, setGatepassLoading] = useState(false);
  const [gatepassError, setGatepassError] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();

  const [currentYardName, setCurrentYardName] = useState("");
  const [currentMapHtml, setCurrentMapHtml] = useState("");
  const fetchDrivers = async () => {
      try {
        setDriverLoading(true);
        const { data } = await apiService.getDrivers("delivery_driver");
        setDrivers(data || []);
        setSelectedDriver(data?.[0] ?? null);
        setDriverError(null);
      } catch (err) {
        setDriverError("Failed to load drivers.");
        setDrivers([]);
        setSelectedDriver(null);
      } finally {
        setDriverLoading(false);
      }
    };
  useEffect(() => {
    if (!isOpen) return;
    fetchDrivers();
  }, [isOpen, navigate]);

  // Initialize selected cars: only same yard + clicked car pre-checked
  useEffect(() => {
    if (!isOpen || !clickedCarId) {
      setSelectedCarIds([]);
      return;
    }

    const clickedCar = cars.find((c) => c.id === clickedCarId);
    if (!clickedCar) {
      setSelectedCarIds([]);
      return;
    }

    const yardName = clickedCar.location;
    const eligibleCars = cars.filter(
      (c) =>
        c.location === yardName &&
        (c.state === "PAYMENT_APPROVED" || c.state === "DELIVERY_GATE_PASS_REJECTED")
    );

    const preChecked = eligibleCars.some((c) => c.id === clickedCarId)
      ? [clickedCarId]
      : [];

    setSelectedCarIds(preChecked);
  }, [isOpen, cars, clickedCarId]);

  useEffect(() => {
    setIsFormValid(!!selectedDriver && selectedCarIds.length > 0);
  }, [selectedDriver, selectedCarIds]);

  // Map & Yard Name
  useEffect(() => {
    if (!isOpen || !cars.length || !yardGroups?.length) {
      setCurrentYardName("");
      setCurrentMapHtml("");
      return;
    }

    const selectedCars = cars.filter((c) => selectedCarIds.includes(c.id));
    if (!selectedCars.length) {
      setCurrentYardName("");
      setCurrentMapHtml("");
      return;
    }

    const yardNames = [...new Set(selectedCars.map((c) => c.location))];
    if (yardNames.length > 1) {
      setCurrentYardName("Multiple yards selected");
      setCurrentMapHtml("");
      return;
    }

    const yardName = yardNames[0];
    const yardGroup = yardGroups.find((g) => g.yardName === yardName);
    setCurrentYardName(yardName);
    setCurrentMapHtml(yardGroup?.YardMap ?? "");
  }, [selectedCarIds, cars, yardGroups, isOpen]);

  const handleSelectCar = (id) => {
    setSelectedCarIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleRequest = async () => {
    if (!selectedCarIds.length || !selectedDriver) {
      setGatepassError("Please select at least one car and a driver.");
      return;
    }

    setGatepassLoading(true);
    setGatepassError(null);

    try {
      const selectedCars = cars.filter((c) => selectedCarIds.includes(c.id));

      for (const car of selectedCars) {
        const payload = {
          carId: car.originalCarId,
          auctionId: car.auctionId,
          driverId: selectedDriver.id,
          driverName: selectedDriver.name,
          driverPhone: selectedDriver.phone || "N/A",
          driverAddress: selectedDriver.address || "N/A",
          notes: "approve",
        };
        await apiService.createDeliveryGatepass(payload);
      }

      onRequestGatepass(selectedCarIds);
      onClose();
    } catch (err) {
      setGatepassError(err.response?.data?.message || "Failed to create gatepass.");
    } finally {
      setGatepassLoading(false);
    }
  };

  useEffect(() => {
    setProfilePicture(selectedDriver?.imageUrl ?? null);
  }, [selectedDriver]);

  const handleOpenAddDriverModal = () => setIsAddDriverModalOpen(true);
  const handleCloseAddDriverModal = () => {
    fetchDrivers()
    setIsAddDriverModalOpen(false)};

  if (!isOpen) return null;

  // Filter cars to show: only from the same yard as clicked car
  const clickedCar = cars.find((c) => c.id === clickedCarId);
  const yardToShow = clickedCar?.location ?? "";
  const carsToShow = yardToShow
    ? cars.filter((c) => c.location === yardToShow)
    : [];

  return (
    <div className="request-delivery-gate-pass-modal">
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="model-header">
            <p>Request Delivery Gatepass</p>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>

          <div className="model-body">
            {gatepassLoading && <Loading />}

            <div className="right-part">
              <div className="evaluator-section">
                {profilePicture ? (
                  <div className="img-view">
                    <div className="profile-pic">
                      <img src={profilePicture} alt="" width="50%" className="uploadModel-img" />
                    </div>
                    <div>
                      <p className="mobile-selected-evaluator">
                        For <div className="spanElement">{selectedDriver.name}</div>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="profile-pic">
                    <img src={pro} alt="" width="50%" />
                  </div>
                )}

                <div className="evaluator-section-part">
                  {selectedDriver && (
                    <p className="selected-evaluator">
                      For <div className="spanElement">{selectedDriver.name}</div>
                    </p>
                  )}
                  {driverLoading ? (
                    <p>Loading drivers...</p>
                  ) : driverError ? (
                    <p className="error">{driverError}</p>
                  ) : (
                    <DriverDropdown
                      drivers={drivers}
                      selectedDriver={selectedDriver}
                      onSelectDriver={setSelectedDriver}
                      onAddDriver={handleOpenAddDriverModal}
                    />
                  )}
                </div>
              </div>

              <div className="map-container">
                <div
                  className="map-wrapper"
                  onClick={() => window.open(getMapUrl(yardGroups), "_blank")}
                >
                  {currentMapHtml ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: buildCleanMapIframe(currentMapHtml) }}
                      className="map-iframe"
                    />
                  ) : (
                    <div className="map-placeholder">
                      <p>
                        {currentYardName
                          ? `${currentYardName} – map not available`
                          : "Select a car to see the yard"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="yard-name-wrapper">
                  <img src={loc} alt="" className="loc-card" />
                  <p className="yard-name">{currentYardName || "—"}</p>
                </div>
              </div>
            </div>

            <div className="left-part">
              <div className="cars-list">
                {carsToShow.length ? (
                  carsToShow.map((car) => (
                    <div key={car.id} className="car-card modal-car-card">
                      <input
                        type="checkbox"
                        checked={selectedCarIds.includes(car.id)}
                        onChange={() => handleSelectCar(car.id)}
                        className="car-checkbox"
                        disabled={
                          car.state !== "PAYMENT_APPROVED" &&
                          car.state !== "DELIVERY_GATE_PASS_REJECTED"
                        }
                      />
                      <div className="car-image-wrapper">
                        <ImageWithFallback
                          src={car.evaluationImages?.[0]?.url ?? "https://via.placeholder.com/150"}
                          alt={`${car.brand} ${car.model}`}
                          className="car-image"
                        />
                      </div>
                      <div className="car-details">
                        <h3 className="car-title">
                          {car.year} {car.brand} {car.model}
                        </h3>
                        <div className="car-specs">
                          {car.km.toLocaleString()} kms • {car.fuel} • {car.transmission}
                        </div>
                        <div className="car-tags">
                          {car.rc ? (
                            <img src={rc_yes} alt="RC Yes" className="rc" />
                          ) : (
                            <img src={rc_no} alt="RC No" className="rc" />
                          )}
                          <span className="rto-tag">{car.rto}</span>
                          <span className="owner-tag">{car.owner}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No cars available in this yard.</p>
                )}
              </div>

              {gatepassError && <p className="error">{gatepassError}</p>}
              {driverError && <p className="error">{driverError}</p>}

              <Button
                variant="primary"
                disabled={gatepassLoading || !isFormValid}
                className="request-gate-pass-btn"
                onClick={handleRequest}
              >
                {gatepassLoading ? "Requesting..." : "Request Delivery Gatepass"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AddDriver
        isOpen={isAddDriverModalOpen}
        onClose={handleCloseAddDriverModal}
        onDriverAdded={() => {}}
        driverType="delivery_driver"
      />
    </div>
  );
};